/**
 * AddPicker — the overlay behind WhoHasAccessDialog's "+ Add" buttons.
 *
 * NOT ANCHORED, and that is the fix rather than an aesthetic choice. It used
 * to be `absolute top-full` inside the dialog's `overflow-y-auto` pane, which
 * failed in two ways once a folder had enough grants to push an Add button
 * toward the bottom of that pane:
 *
 *   · CLIPPED — the pane's bottom edge is a hard boundary, so a ~300px
 *     popover opened near it was sliced off mid-list. Worst on the LAST
 *     section ("No access"), whose Add sits at the very bottom.
 *   · PUSHED THE VIEW — an absolutely-positioned child still contributes to
 *     its scroll container's scrollable height, so merely opening the picker
 *     grew the scroll area beneath it.
 *
 * Flipping upward (the `findScrollParent` trick the sharing dropdown uses)
 * fixes neither on a short viewport, where neither direction has room. A
 * portal removes the popover from the clipping/scrolling context entirely, so
 * both failures stop being possible rather than becoming less likely.
 *
 * It is still ANCHORED to its Add button on a desktop — the popover reads as
 * belonging to the section it grants on, which a centred dialog loses — but it
 * is positioned in VIEWPORT coordinates from the trigger's rect rather than by
 * `absolute` inside the pane. That keeps the anchoring and removes the clip:
 *
 *   · flips ABOVE the trigger when the space below is too small
 *   · shifts horizontally to stay on screen instead of running off the edge
 *   · clamps its own max-height to the room actually available, so a cramped
 *     viewport shortens the LIST rather than slicing the panel
 *
 * Phones get a bottom sheet instead: a 320px popover pinned to a small button
 * is not a touch target, and the on-screen keyboard would cover it the moment
 * the filter is focused. 768px is the same boundary `useIsPhone` uses to pick
 * the mobile shell, so the two cannot disagree.
 *
 * `svh`, not `vh` or `dvh`: `svh` is the SMALL viewport height — the size
 * with the browser's toolbars SHOWING. It never changes as they hide and
 * reveal on scroll, so the sheet cannot resize under the reader mid-gesture,
 * which `dvh` does by design. `vh` is worse still: it ignores the chrome and
 * hides the bottom of the list behind it.
 *
 * Renders three sub-tabs (Role / Team / Member) with a filter input and a
 * scrolling list. Clicking a row calls `onPick(type, targetId, label, perm)`
 * and the parent handles the upsert + close.
 *
 * Targets that are already explicitly granted at the active permission are
 * disabled with an "already added" hint so the picker doesn't issue no-op
 * upserts.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useIsDesktop } from '@papan/core-ui/ui'
import type {
  AccessTag,
  MemberInfo,
  Permission,
  TagType,
  TeamInfo,
} from './WhoHasAccessDialog.types.js'
import { ROLE_LABELS } from './WhoHasAccessDialog.types.js'

const PICKER_TABS: Array<{ key: TagType; label: string }> = [
  { key: 'role',   label: 'Role'   },
  { key: 'team',   label: 'Team'   },
  { key: 'member', label: 'Member' },
]

const ADDABLE_ROLES = ['admin', 'member', 'viewer', 'guest', 'noaccess']

/** Panel width on a desktop; the phone sheet is full-bleed. */
const PANEL_W = 320
/** Gap between trigger and panel, and the smallest edge margin we allow. */
const GAP = 6
const MARGIN = 8
/** Below this much room, opening downward is not worth it — flip instead. */
const MIN_BELOW = 220
/**
 * The panel's RESTING height, so switching sub-tabs never resizes it.
 *
 * A picker sized to its content changes height on every tab change — Role has
 * five rows, Team often none, Member however many people exist. Opening
 * downward that is survivable: the top edge is pinned, so the bottom grows away
 * from where you are looking. FLIPPED it is not, because the bottom is pinned
 * and the reflow moves the TOP — the tabs and the title jump under the cursor
 * mid-interaction.
 *
 * Fixing the height removes the reflow rather than animating it: the list
 * scrolls inside a constant frame, so a tab change is only the sliding
 * indicator and a content swap. `min(RESTING, available)` keeps it honest on a
 * cramped viewport — the frame shrinks and the list shrinks with it.
 */
const PANEL_H = 300
/** Sheet transition duration. Must equal the CSS duration below — the
 *  unmount is timed against it. */
const SHEET_MS = 300

export function AddPicker({
  activePermission,
  tags,
  allTeams,
  allMembers,
  initialTab = 'role',
  tierLabel,
  title,
  closeLabel = 'Close',
  anchorEl,
  onPick,
  onClose,
}: {
  activePermission: Permission
  tags: AccessTag[]
  allTeams: TeamInfo[]
  allMembers: MemberInfo[]
  /** Sub-tab to open on first render (Role / Team / Member). Lets a caller with
   *  dedicated +Role / +Team / +Member buttons land on the pressed tab. */
  initialTab?: TagType
  /** The "+ Add" button this popover belongs to, captured from the click that
   *  opened it (`e.currentTarget`) rather than a ref — the All-permissions view
   *  renders one Add per section inside a `.map()`, where a per-item ref would
   *  force a component split. Desktop positions against its rect; the phone
   *  sheet ignores it.
   *
   *  REQUIRED, not optional (#1240): an optional anchor let
   *  ShareInternallyDialog omit it, and the desktop panel then stayed hidden
   *  forever — the button clicked and nothing appeared. `null` is still
   *  accepted, and the panel then centres itself instead of hiding (see the
   *  placement effect), but a caller has to SAY so. */
  anchorEl: HTMLElement | null
  /** Human label for the tier being granted ("Read-only", "No access", …).
   *  Passed in rather than re-derived: the parent's `labelFor` varies the
   *  wording by share-cap, and a second mapping here would drift from it. */
  tierLabel?: string
  /** The whole heading, already translated, for a caller with no tier to name
   *  (Share internally: "Add member"). It replaces "Add to <tier>" as the
   *  panel's accessible name and, on a PHONE, is the sheet's title row with
   *  its Close button — without one the sheet had neither, could only be
   *  swiped away, and announced itself as "Add to readcopy". On a desktop it
   *  draws no caption: the popover hangs from the button that already says it. */
  title?: string
  /** The phone sheet's Close button label, already translated. */
  closeLabel?: string
  onPick: (type: TagType, targetId: string, label: string, permission: Permission) => void
  onClose: () => void
}) {
  const [tab, setTab] = useState<TagType>(initialTab === 'app' ? 'role' : initialTab)
  const [search, setSearch] = useState('')
  const popRef = useRef<HTMLDivElement>(null)

  // Portaled out of the dialog, so the CSS variables the `themed-*` classes
  // read are NOT inherited — the theme class has to be applied here by hand.
  // Same rule the dialog itself and the PDApp picker follow.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const isDesktop = useIsDesktop()
  /** The panel's name and title: the caller's own `title`, else the tier. */
  const heading = title ?? (tierLabel ? `Add to ${tierLabel}` : undefined)

  /**
   * Slide-up entry for the sheet, matching `MobileFileSheet` — same 300ms and
   * the same `cubic-bezier(0.32,0.72,0,1)`, so the two sheets in this flow feel
   * like one system rather than two.
   *
   * DOUBLE rAF is load-bearing, and is why this is not a CSS keyframe: the
   * off-screen `translateY(100%)` frame has to PAINT before `shown` flips, or
   * the browser coalesces both into one paint and the transition never plays —
   * the sheet just appears. A single rAF sometimes lands in the same paint.
   */
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (isDesktop) return
    let r2 = 0
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setShown(true)) })
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2) }
  }, [isDesktop])

  /**
   * DISMISS with an outro, mirroring `MobileFileSheet`'s slide-down.
   *
   * The parent owns whether this is mounted, so an exit animation is only
   * possible if the sheet delays telling it — hence: drop `shown` (the sheet
   * slides out, the scrim fades), then call `onClose` once the transition has
   * run. `SHEET_MS` matches the CSS duration; a mismatch either cuts the
   * animation short or leaves a frozen sheet on screen afterwards.
   *
   * `closingRef` makes it idempotent — Escape, a backdrop tap and an outside
   * mousedown can all land inside the same 300ms, and three timers would call
   * `onClose` three times.
   *
   * Desktop closes immediately: the popover has no outro to play, and delaying
   * it would just feel unresponsive.
   */
  /**
   * Swipe-down-to-dismiss.
   *
   * The grab handle below has always been drawn and was never wired, which is
   * the failure `MobileSheet`'s own docblock names: a menu that LOOKS like a
   * sheet but cannot be swiped away is worse than one that does not pretend.
   * Every other sheet on the phone dismisses this way, so the affordance was
   * making a promise the rest of the app keeps.
   *
   * Mirrors MobileFileSheet: `dragY` overrides the resting transform while a
   * finger is down, the transition is suppressed for the duration (so the
   * sheet tracks the finger instead of easing behind it), and a release past
   * the threshold finishes the gesture off-screen rather than snapping.
   */
  const [dragY, setDragY] = useState(0)
  const dragging = useRef(false)
  const startY = useRef(0)
  const dragYRef = useRef(0)

  const closingRef = useRef(false)
  const requestClose = useCallback(() => {
    if (isDesktop) { onClose(); return }
    if (closingRef.current) return
    closingRef.current = true
    setShown(false)
    setTimeout(onClose, SHEET_MS)
  }, [isDesktop, onClose])

  const onDragStart = (e: React.PointerEvent) => {
    if (isDesktop) return
    dragging.current = true; startY.current = e.clientY; dragYRef.current = 0
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragging.current) return
    // Downward only. An upward drag on a bottom sheet should do nothing rather
    // than lift it off its edge.
    const dy = Math.max(0, e.clientY - startY.current)
    dragYRef.current = dy; setDragY(dy)
  }
  const onDragEnd = () => {
    if (!dragging.current) return
    dragging.current = false
    if (dragYRef.current > 90) {
      // Past the threshold: carry on in the direction the finger was going.
      //
      // `setDragY(0)` is REQUIRED, not tidying. The transform reads the STATE
      // (`dragY > 0 ? translateY(dragY) : …`), so clearing only the ref left
      // `dragY` at the release distance and the expression kept evaluating to
      // the same translate. Nothing changed, so nothing transitioned: the
      // sheet froze where the finger left it, the scrim faded around it for
      // 300ms, and then it vanished on unmount. Clearing the state hands the
      // transform back to the `shown` branch, which `requestClose` has just
      // flipped to `translateY(100%)` — so the release continues downward
      // instead of stopping dead.
      dragYRef.current = 0
      setDragY(0)
      requestClose()
    } else {
      setDragY(0); dragYRef.current = 0
    }
  }

  // Escape closes the PICKER, not the whole dialog. Capture phase +
  // stopPropagation because the parent listens on the same key and would
  // otherwise tear down Who-has-access underneath it, losing the reader's
  // place after they had already added grants.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      requestClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [requestClose])


  /**
   * Viewport-space placement for the desktop popover. Recomputed on scroll
   * (capture phase — the dialog's pane scrolls and scroll does not bubble)
   * and on resize, so the panel tracks its trigger instead of detaching.
   *
   * `useLayoutEffect`, so the first paint is already in the right place: the
   * measure-then-move version renders once at the wrong coordinates and the
   * panel visibly jumps.
   *
   * NO ANCHOR NEVER MEANS HIDDEN (#1240). The panel stays `invisible` until
   * `pos` is set, so an effect that bailed out without an anchor left it
   * mounted and invisible for good — ShareInternallyDialog passed none, and its
   * "Add member" did nothing on every desktop. Without an anchor, or with one
   * that has left the document (its rect is all zeros, which would park the
   * panel in the top-left corner, detached from anything), the panel is
   * centred in the viewport instead: in the wrong place is a bug someone
   * reports; invisible is a control nobody can find.
   */
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; maxH: number } | null>(null)
  useLayoutEffect(() => {
    if (!isDesktop) { setPos(null); return }
    const place = () => {
      if (!anchorEl || !anchorEl.isConnected) {
        const maxH = Math.max(160, Math.min(420, window.innerHeight - MARGIN * 2))
        const h = Math.min(PANEL_H, maxH)
        setPos({
          top: Math.max(MARGIN, Math.round((window.innerHeight - h) / 2)),
          left: Math.max(MARGIN, Math.round((window.innerWidth - PANEL_W) / 2)),
          maxH,
        })
        return
      }
      const a = anchorEl.getBoundingClientRect()
      const below = window.innerHeight - a.bottom - GAP - MARGIN
      const above = a.top - GAP - MARGIN
      // Flip only when below is genuinely cramped AND above is roomier —
      // otherwise a panel that merely wants more space would flip on every
      // open, which reads as jitter.
      const flip = below < MIN_BELOW && above > below
      const maxH = Math.max(160, Math.min(420, flip ? above : below))
      // Horizontal bounds are the HOST DIALOG when there is one, not the
      // viewport: a right-hand "+ Add" is nowhere near the screen edge, so a
      // viewport-only clamp left the panel hanging outside the dialog over the
      // page. Falls back to the viewport when the marker is absent, and when
      // the box is narrower than the panel (a clamp with an inverted range
      // would otherwise pin it to the left edge).
      const host = anchorEl.closest('[data-picker-bounds]')?.getBoundingClientRect()
      const usable = host && host.width >= PANEL_W + MARGIN * 2 ? host : null
      const minL = usable ? usable.left + MARGIN : MARGIN
      const maxL = (usable ? usable.right : window.innerWidth) - PANEL_W - MARGIN
      // Prefer left-aligned with the trigger; right-align when that would
      // overrun; clamp last so the result is always inside the box.
      let left = a.left
      if (left + PANEL_W > maxL + PANEL_W) left = a.right - PANEL_W
      left = Math.max(minL, Math.min(left, maxL))
      // A FLIPPED panel is pinned by its BOTTOM, never by a top computed from
      // `maxH`. `maxH` is the space available, not the height the panel takes:
      // hanging it from `a.top - maxH` left a short list floating ~170px above
      // its own button, measurably on-screen and visibly detached from the
      // section it grants on.
      setPos(flip
        ? { bottom: window.innerHeight - a.top + GAP, left, maxH }
        : { top: a.bottom + GAP, left, maxH })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [isDesktop, anchorEl])

  /**
   * Desktop: focus the search box once the panel has been PLACED, so typing a
   * name works straight after "+ Add". Not `autoFocus`: React applies that
   * before the placement effect has set `pos`, while the panel is still
   * `invisible`, and a browser refuses focus to a hidden element — so focus
   * stayed on the "+ Add" button. This runs after the commit that drops
   * `invisible`. Once per open: re-placement on scroll or resize must not pull
   * focus back from a tab the reader has moved to. Never on a phone, where it
   * raises the keyboard over the list the sheet exists to show.
   */
  const searchRef = useRef<HTMLInputElement>(null)
  const focusedRef = useRef(false)
  useEffect(() => {
    if (!isDesktop || !pos || focusedRef.current) return
    focusedRef.current = true
    searchRef.current?.focus()
  }, [isDesktop, pos])

  /**
   * …and give it back on close. The panel is portaled to `document.body`, so
   * when it unmounts with focus inside (Escape, a pick, the Close button) the
   * browser drops focus to `<body>` and the next Tab starts at the top of the
   * page behind the dialog. Return it to the "+ Add" button (else to whatever
   * had focus when the picker opened). A LAYOUT cleanup, because it runs before
   * the portal's node is removed, while focus can still be seen inside it; only
   * when focus is inside (or already on <body>, see below), so a close caused
   * by clicking elsewhere leaves focus where that click put it (React's commit
   * also restores an outside element that stays in the document; this guard
   * doesn't lean on that). StrictMode's
   * mount/unmount/mount runs this cleanup before anything was focused, so it
   * does nothing there.
   */
  const anchorElRef = useRef(anchorEl)
  anchorElRef.current = anchorEl
  useLayoutEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = popRef.current
    return () => {
      const active = document.activeElement
      // Inside the panel, or already dropped to <body>: Safari and Firefox on
      // macOS don't focus a clicked button, so a pick's mousedown blurs the
      // search box to <body> before the panel unmounts.
      if (!panel || !(active === null || active === document.body || panel.contains(active))) return
      const back = anchorElRef.current?.isConnected ? anchorElRef.current : opener?.isConnected ? opener : null
      back?.focus({ preventScroll: true })
    }
  }, [])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) requestClose()
    }
    // Defer registration so the click that opened the picker doesn't immediately close it.
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler) }
  }, [requestClose])

  // Already-explicit-at-this-perm — disabled in the picker (no-op).
  const explicitHere = useMemo(() => {
    const set = new Set<string>()
    for (const t of tags) {
      if (!t.inherited && t.permission === activePermission) set.add(`${t.type}:${t.targetId}`)
    }
    return set
  }, [tags, activePermission])

  const q = search.trim().toLowerCase()

  /**
   * Filtered + ordered member list.
   *
   * Pending invitees FIRST: they are who an admin is most often looking for
   * (granting a new hire access while onboarding them), and floating them also
   * answers "who still hasn't accepted?" without a trip to Settings. `sort` is
   * stable in every engine we target, so the server's order survives within
   * each group.
   *
   * Memoised because this runs on EVERY render — including every keystroke in
   * the search box — and `.sort()` mutates, so it must never touch the `filter`
   * source directly.
   */
  const visibleMembers = useMemo(() => (
    allMembers
      .filter(m => !q || m.email.toLowerCase().includes(q) || (m.name?.toLowerCase().includes(q)))
      .sort((a, b) => Number(b.status === 'invited') - Number(a.status === 'invited'))
  ), [allMembers, q])

  return createPortal(
    // NO themeClass on this wrapper. It is full-bleed, and the theme class
    // paints a SOLID background — which covered the host dialog completely, so
    // the list you are granting on vanished the moment the picker opened. The
    // class belongs on the PANEL. (Same rule the project's theming notes give
    // for modal backdrops.)
    <div
      className={
        isDesktop
          // Desktop: no scrim. The popover belongs to the section behind it and
          // dimming that context is exactly what made the centred version worse
          // than the original. `pointer-events-none` lets clicks reach the
          // dialog; the panel re-enables them for itself.
          ? 'fixed inset-0 z-[120] pointer-events-none'
          : 'fixed inset-0 z-[120] flex items-end'
      }
    >
      {!isDesktop && (
        <div
          className={`absolute inset-0 bg-black/50 ${
            dragging.current ? '' : 'transition-opacity duration-300 ease-out'
          } motion-reduce:transition-none`}
          /* The scrim FADES AS THE SHEET IS PULLED DOWN, matching
             MobileFileSheet and MobileSheet, which both compute it this way.
             Pinned at full strength it made the same gesture give different
             feedback in two sheets one flow apart: the panel followed the
             finger while the dim behind it did not move, so the drag read as
             sliding a panel over a fixed wall rather than dismissing it.
             `/400` is the same divisor the other two use, so the three fade at
             one rate. */
          style={{ opacity: shown ? Math.max(0, 1 - dragY / 400) : 0 }}
          onClick={requestClose}
        />
      )}
      <div
        ref={popRef}
        role="dialog"
        aria-modal={isDesktop ? undefined : 'true'}
        aria-label={heading ?? `Add to ${activePermission}`}
        style={!isDesktop
          ? { transform: dragY > 0 ? `translateY(${dragY}px)` : (shown ? 'translateY(0)' : 'translateY(100%)') }
          : pos
          ? {
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: PANEL_W,
              // HEIGHT, not maxHeight: a max lets the panel size to content,
              // which is the reflow this constant exists to stop.
              height: Math.min(PANEL_H, pos.maxH),
            }
          : undefined}
        className={
          `${themeClass} pointer-events-auto themed-bg-secondary shadow-xl flex flex-col `
          + (isDesktop
            // Hidden until measured — one frame at (0,0) is a visible jump.
            ? `fixed rounded-md border themed-border ${pos ? '' : 'invisible'}`
            : `relative w-full h-[52svh] max-h-[85svh] rounded-t-2xl border-t themed-border pb-[env(safe-area-inset-bottom)] ${
                dragging.current ? '' : 'transition-transform duration-300 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]'
              } motion-reduce:transition-none`)
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grab handle — phone only. Says "this sheet is dismissible" in the
            vocabulary a touch user already reads, without spending a row on a
            Close button. */}
        {!isDesktop && (
          <div
            className="pt-2.5 pb-1.5 flex justify-center shrink-0 touch-none cursor-grab active:cursor-grabbing"
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          >
            {/* `touch-none` on the surface, not the sheet: without it the
                browser claims the vertical gesture for its own scroll and the
                pointermove events stop arriving mid-drag. */}
            <span className="h-1 w-10 rounded-full themed-bg-tertiary" />
          </div>
        )}

        {/* Which tier this grant lands on. On a phone it is the sheet's title
            and carries a real close target; on a desktop it is a quiet caption,
            because the popover already points at the section it belongs to.
            A caller's own `title` is a phone-only row (see the prop). */}
        {heading && (tierLabel || !isDesktop) && (
          <div className="flex items-center justify-between gap-2 px-4 md:px-3 pb-2.5 md:py-2 pt-0.5 md:pt-2 border-b themed-border-secondary shrink-0">
            <span className="text-base md:text-xs font-semibold md:font-medium themed-text truncate">{heading}</span>
            {!isDesktop && (
              <button
                onClick={requestClose}
                aria-label={closeLabel}
                className="h-9 w-9 -mr-2 flex items-center justify-center rounded-full themed-text-muted active:themed-bg-tertiary shrink-0"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

      {/* Sub-tabs.
          The active marker is ONE element that slides, not a per-tab border:
          a border toggling on and off cannot animate between two positions,
          because they are two different elements. Every tab is `flex-1`, so
          the geometry is exact arithmetic — width 1/n, offset index × 100% —
          with no measuring, no ref per tab, and nothing to resync when the
          panel is resized or reflowed. */}
      <div className="relative flex border-b themed-border-secondary shrink-0">
        {PICKER_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            aria-pressed={tab === key}
            className={`flex-1 py-3 md:py-1.5 text-sm md:text-xs transition-colors ${tab === key ? 'themed-text font-medium' : 'themed-text-muted hover:themed-text'}`}
          >
            {label}
          </button>
        ))}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 h-0.5 rounded-full bg-purple-500 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
          style={{
            width: `${100 / PICKER_TABS.length}%`,
            transform: `translateX(${PICKER_TABS.findIndex(t => t.key === tab) * 100}%)`,
          }}
        />
      </div>

      {/* Search (only useful for member; harmless on others) */}
      <div className="p-3 md:p-2 border-b themed-border-secondary shrink-0">
        <input
          // Focused on a desktop by the effect after placement — see searchRef.
          ref={searchRef}
          type="text"
          placeholder={tab === 'member' ? 'Search members...' : `Filter ${tab}s...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 md:px-2 py-2.5 md:py-1 text-base md:text-xs themed-bg-primary themed-text rounded-lg md:rounded border themed-border-secondary focus:outline-none focus:ring-1 focus:ring-purple-500"
        />
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto py-1 overscroll-contain">
        {tab === 'role' && (
          ADDABLE_ROLES
            .filter(r => !q || (ROLE_LABELS[r] ?? r).toLowerCase().includes(q))
            .map(r => {
              const disabled = explicitHere.has(`role:${r}`)
              const label = `role:${ROLE_LABELS[r] ?? r}`
              return (
                <PickerRow
                  key={r}
                  label={ROLE_LABELS[r] ?? r}
                  disabled={disabled}
                  onClick={() => !disabled && onPick('role', r, label, activePermission)}
                />
              )
            })
        )}
        {tab === 'team' && (
          allTeams.length === 0 ? (
            <p className="px-3 py-2 text-xs themed-text-muted">No teams in this org</p>
          ) : (
            allTeams
              .filter(t => !q || t.name.toLowerCase().includes(q))
              .map(t => {
                const disabled = explicitHere.has(`team:${t.id}`)
                return (
                  <PickerRow
                    key={t.id}
                    label={t.name}
                    disabled={disabled}
                    onClick={() => !disabled && onPick('team', t.id, `team:${t.name}`, activePermission)}
                  />
                )
              })
          )
        )}
        {tab === 'member' && (
          visibleMembers.length === 0 ? (
            // TWO different empty states. Collapsing them makes a search that
            // matches nothing claim the org is empty — which reads as data
            // loss rather than "no match". The org-empty copy also counts
            // invitees, since they are listed here and are valid grant targets.
            <p className="px-3 py-2 text-xs themed-text-muted">
              {allMembers.length === 0
                ? 'No members or pending invites in this org'
                : `No members match “${search.trim()}”`}
            </p>
          ) : (
            visibleMembers
              .map(m => {
                const disabled = explicitHere.has(`member:${m.email}`)
                return (
                  <PickerRow
                    key={m.email}
                    // A pending invite has `name = email` server-side, so the
                    // naive form printed "a@b.com (a@b.com)".
                    label={m.name && m.name !== m.email ? `${m.name} (${m.email})` : m.email}
                    disabled={disabled}
                    // An invitee is a legitimate target — the grant is staged
                    // and takes effect the moment they accept — but they are
                    // not in the org yet, so label them rather than listing
                    // them indistinguishably from members.
                    badge={m.status === 'invited' ? 'Invited' : undefined}
                    onClick={() => !disabled && onPick('member', m.email, m.email, activePermission)}
                  />
                )
              })
          )
        )}
      </div>
      </div>
    </div>,
    document.body,
  )
}

function PickerRow({ label, disabled, badge, onClick }: { label: string; disabled: boolean; badge?: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full px-4 md:px-3 py-3 md:py-1.5 min-h-11 md:min-h-0 text-left text-sm md:text-xs flex items-center gap-2 ${
        disabled
          ? 'themed-text-muted cursor-not-allowed'
          : 'themed-text hover:themed-bg-tertiary active:themed-bg-tertiary cursor-pointer'
      }`}
    >
      <span className="truncate" title={label}>{label}</span>
      {/* `already added` wins the right slot: it explains why the row is
          disabled, which is the more urgent fact. */}
      {disabled
        ? <span className="ml-auto text-[11px] md:text-[10px] themed-text-muted shrink-0">already added</span>
        : badge && <span className="ml-auto shrink-0 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300">{badge}</span>}
    </button>
  )
}
